<template>
  <div class="content">
    <div class="content-library-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-content-library">

          <!-- DETAIL VIEW: SecurePDFViewer for a selected secure resource -->
          <template v-if="selectedResource">
            <nav class="glad-breadcrumb">
              <a href="#" @click.prevent="closeDetail" class="glad-breadcrumb__link">
                ← Back to library
              </a>
            </nav>

            <header class="glad-detail-header">
              <span class="glad-eyebrow">Content Library</span>
              <h1 class="glad-detail-title">{{ selectedResource.title }}</h1>
              <p v-if="selectedResource.course_name" class="glad-detail-sub">
                from <strong>{{ selectedResource.course_name }}</strong>
              </p>
            </header>

            <div v-if="signedUrlLoading" class="glad-detail-loading">
              <span class="glad-spinner glad-spinner--sm"></span>
              <span>Preparing protected document…</span>
            </div>

            <div v-else-if="signedUrlError" class="glad-detail-error">
              {{ signedUrlError }}
            </div>

            <SecurePDFViewer
              v-else
              :resource-id="selectedResource.id"
              :access-mode="selectedResource.access_mode"
              :user-email="userEmail"
              :title="selectedResource.title" />
          </template>

          <!-- LIST VIEW: grid of available secure assets -->
          <template v-else>
            <header class="glad-library-header">
              <p class="glad-eyebrow">Restricted to authorized learners</p>
              <h1 class="glad-library-title">Content Library</h1>
              <p class="glad-library-sub">
                Course documents and references. Each item is watermarked
                with your email and tracked per session.
              </p>
            </header>

            <div class="glad-library-toolbar">
              <div class="glad-search">
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
                <input
                  v-model="search"
                  @input="onSearch"
                  type="search"
                  placeholder="Search library…"
                  aria-label="Search library" />
              </div>

              <div class="glad-filter" role="group" aria-label="Filter">
                <button
                  v-for="opt in accessFilters"
                  :key="opt.value"
                  type="button"
                  class="glad-filter__chip"
                  :class="{ 'glad-filter__chip--active': accessFilter === opt.value }"
                  @click="setAccessFilter(opt.value)">
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <div v-if="loading" class="glad-loading">
              <span class="glad-spinner"></span>
            </div>

            <div v-else-if="filteredItems.length === 0" class="glad-empty">
              <p v-if="search || accessFilter !== 'all'">No documents match your filters.</p>
              <p v-else>No content has been added to the library yet.</p>
            </div>

            <div v-else class="glad-library-grid">
              <article
                v-for="item in filteredItems"
                :key="item.id"
                class="glad-library-card"
                :class="{ 'glad-library-card--clickable': item.is_secure }"
                role="button"
                tabindex="0"
                @click="onItemClick(item)"
                @keyup.enter="onItemClick(item)">

                <div class="glad-library-card__icon" :class="iconClass(item)">
                  <span v-if="item.is_secure" class="glad-library-card__ext">{{ (item.extension || 'PDF').toUpperCase() }}</span>
                  <svg v-else viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                  </svg>
                </div>

                <div class="glad-library-card__body">
                  <h3 class="glad-library-card__title">{{ item.title }}</h3>
                  <p v-if="item.course_name" class="glad-library-card__course">
                    {{ item.course_name }}
                  </p>
                  <div class="glad-library-card__meta">
                    <span
                      class="glad-library-card__pill"
                      :class="'glad-library-card__pill--' + item.access_mode">
                      {{ item.access_mode === 'download_allowed' ? 'Download allowed' : 'View only' }}
                    </span>
                    <span v-if="!item.is_secure" class="glad-library-card__pill glad-library-card__pill--link">
                      External link
                    </span>
                  </div>
                </div>
              </article>
            </div>
          </template>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
import SecurePDFViewer from '@/components/glad/SecurePDFViewer.vue';

export default {
  name: 'ContentLibrary',
  components: { SecurePDFViewer },
  data() {
    return {
      loading: true,
      items: [],
      search: '',
      searchDebounce: null,
      accessFilter: 'all',
      accessFilters: [
        { value: 'all',              label: 'All' },
        { value: 'view_only',        label: 'View only' },
        { value: 'download_allowed', label: 'Downloadable' },
      ],
      selectedResource: null,
      signedUrl: null,
      signedUrlLoading: false,
      signedUrlError: '',
      userEmail: '',
    };
  },
  computed: {
    filteredItems() {
      // Backend already returns filtered set, but we filter again locally so
      // toggling chips feels instant instead of waiting on a round-trip.
      const term = this.search.trim().toLowerCase();
      return this.items.filter(item => {
        if (this.accessFilter !== 'all' && item.access_mode !== this.accessFilter) return false;
        if (term) {
          const hay = (item.title + ' ' + (item.course_name || '')).toLowerCase();
          if (!hay.includes(term)) return false;
        }
        return true;
      });
    },
  },
  created() {
    if (typeof localStorage !== 'undefined') {
      this.userEmail = localStorage.getItem('hot-email') || '';
    }
    this.fetchItems();
  },
  methods: {
    fetchItems() {
      this.loading = true;
      this.$http.get('glad/content')
        .then(resp => {
          const list = (resp && resp.data && Array.isArray(resp.data.data)) ? resp.data.data : [];
          this.items = list;
        })
        .catch(() => { this.items = []; })
        .finally(() => { this.loading = false; });
    },
    onSearch() {
      // Local-only filtering — no debounce needed for a static list.
      // Kept as a no-op handler so we can swap to server-side search later.
    },
    setAccessFilter(value) {
      this.accessFilter = value;
    },
    iconClass(item) {
      if (!item.is_secure) return 'glad-library-card__icon--link';
      if ((item.extension || '').toLowerCase() === 'pdf') return 'glad-library-card__icon--pdf';
      return 'glad-library-card__icon--file';
    },
    onItemClick(item) {
      // External links open in a new tab without going through the secure viewer.
      if (!item.is_secure) {
        if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      this.openDetail(item);
    },
    openDetail(item) {
      this.selectedResource = item;
      this.signedUrl = null;
      this.signedUrlError = '';
      this.signedUrlLoading = true;
      this.$http.get('glad/secure-url', { params: { resource_id: item.id } })
        .then(resp => {
          this.signedUrl = resp.data.url;
          // Server confirms the access mode in case the row changed
          if (resp.data.access_mode) {
            this.selectedResource = { ...this.selectedResource, access_mode: resp.data.access_mode };
          }
        })
        .catch(err => {
          let msg = 'We could not prepare this document. Please try again.';
          if (err && err.response && err.response.data && err.response.data.message) {
            msg = err.response.data.message;
          }
          this.signedUrlError = msg;
        })
        .finally(() => { this.signedUrlLoading = false; });
    },
    closeDetail() {
      this.selectedResource = null;
      this.signedUrl = null;
      this.signedUrlError = '';
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.content-library-wrap { width: 100%; }
.content-library-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-content-library {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Headers ------------------------------------------------------------------ */
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-library-header { margin-bottom: 1.5rem; }
.glad-library-title,
.glad-detail-title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-library-sub,
.glad-detail-sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
  strong { color: var(--glad-ink); font-weight: 600; }
}
.glad-detail-header { margin-bottom: 1.25rem; }

/* Breadcrumb --------------------------------------------------------------- */
.glad-breadcrumb { margin-bottom: 1rem; }
.glad-breadcrumb__link {
  font-size: 0.85rem;
  color: var(--glad-text-soft);
  text-decoration: none;
  &:hover { color: var(--glad-ink); }
}

/* Toolbar ------------------------------------------------------------------ */
.glad-library-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.5rem;
}

.glad-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.85rem;
  background: var(--glad-paper-2);
  border-radius: 999px;
  height: 38px;
  flex: 1;
  max-width: 360px;
  color: var(--glad-text-muted);

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: var(--glad-font-sans);
    font-size: 0.85rem;
    color: var(--glad-ink);
    &::placeholder { color: var(--glad-text-muted); }
  }
}

.glad-filter {
  display: inline-flex;
  gap: 0.4rem;
  padding: 4px;
  background: var(--glad-paper-2);
  border-radius: 999px;
}
.glad-filter__chip {
  padding: 0.35rem 0.85rem;
  background: transparent;
  border: none;
  border-radius: 999px;
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--glad-text-soft);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover { color: var(--glad-ink); }
  &--active {
    background: var(--glad-surface);
    color: var(--glad-ink);
    box-shadow: 0 1px 3px rgba(15, 17, 21, 0.08);
  }
}

/* Grid --------------------------------------------------------------------- */
.glad-library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.85rem;
}

.glad-library-card {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  cursor: default;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &--clickable { cursor: pointer; }
  &:hover {
    border-color: var(--glad-line-strong);
    box-shadow: 0 6px 18px rgba(15, 17, 21, 0.06);
  }
}

.glad-library-card__icon {
  flex-shrink: 0;
  width: 44px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-family: var(--glad-font-sans);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #fff;

  &--pdf  { background: var(--glad-accent); }
  &--file { background: var(--glad-ink); }
  &--link { background: var(--glad-paper-2); color: var(--glad-text-soft); border: 1px solid var(--glad-line); }
}
.glad-library-card__ext {
  font-size: 0.7rem;
  font-weight: 800;
}

.glad-library-card__body { flex: 1; min-width: 0; }
.glad-library-card__title {
  font-family: var(--glad-font-serif);
  font-size: 0.98rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin: 0 0 0.2rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.glad-library-card__course {
  font-size: 0.78rem;
  color: var(--glad-text-soft);
  margin: 0 0 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.glad-library-card__meta {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.glad-library-card__pill {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.16rem 0.5rem;
  border-radius: 999px;

  &--view_only        { background: rgba(180, 83, 9, 0.10);  color: var(--glad-accent); }
  &--download_allowed { background: rgba(21, 128, 61, 0.10); color: #15803d; }
  &--link             { background: var(--glad-paper-2);     color: var(--glad-text-soft); }
}

/* Detail view -------------------------------------------------------------- */
.glad-detail-loading,
.glad-detail-error {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.5rem;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}
.glad-detail-error { color: var(--glad-danger); }

/* Loading + empty ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
  &--sm { width: 16px; height: 16px; border-width: 2px; }
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}
</style>
